import { AppShape } from "@eave/shared/types/api";
import { WorksheetBaseType } from "@eave/shared/types/api/worksheets";
import { formatCurrency } from "@eave/shared/utils/currency";

import { makeHeaderData } from "web_modules/shared/BaseTable/helpers";

import { IWorksheetState } from "underwriter_dashboard/shared/WorksheetEditor/interfaces";
import { ENUM, WS_NAME } from "underwriter_dashboard/shared/WorksheetEditor/wsDefs";
import { getWsById, getWsByName } from "underwriter_dashboard/shared/WorksheetEditor/wsHelpers";

type AdjustmentComputedValues = {
  readonly section: string;
  readonly type: string;
};

export type Adjustment = WorksheetBaseType &
  AdjustmentComputedValues & {
    description: string;
    paid_to_paid_by: keyof typeof ENUM.ADJUSTMENT_ITEM;
    final_le_amount: string;
    cd_amount: string;
    verified: boolean;
  };

export type AdjustmentFormState = Adjustment & {
  array_index: number;
};

export type AdjustmentWorksheetResponse = {
  id: string;
  version: string;
  items: string[];
};

export type AdjustmentWorksheet = {
  id: string;
  version: string;
  items: IWorksheetState[];
};

export const adjustmentsHeaderData = () => {
  return makeHeaderData({
    section: "Section",
    type: "Type",
    description: "Description",
    paid_to_paid_by: "Paid to / Paid by",
    final_le_amount: "Final LE amount",
    cd_amount: "CD amount",
    verified: "Verified",
  });
};

export const adjustmentToRow = (p: Adjustment) => ({
  ...p,
  final_le_amount: p.final_le_amount ? formatCurrency(p.final_le_amount, 2, 2) : null,
  cd_amount: p.cd_amount ? formatCurrency(p.cd_amount, 2, 2) : null,
  verified: p.verified === null ? null : p.verified ? "Yes" : "No",
});

/** Returns the adjustments worksheet with nested slices' instances mapped to the top-level
 * @param  {AppShape} app
 * @param  {IWorksheetState[]} worksheets
 * @param  {React.Dispatch<any>} setWorksheets
 */
export const loadAdjustments = (
  app: AppShape,
  worksheets: IWorksheetState[],
  setWorksheets: React.Dispatch<any>,
) => {
  const adjustmentsWs = getWsByName(worksheets, WS_NAME.ADJUSTMENTS);

  if (!adjustmentsWs || !adjustmentsWs.instance) {
    throw new Error(`could not load adjustments worksheet on app ${app.id}`);
  }

  const {
    instance: { version: instanceVersion, items },
  } = adjustmentsWs;

  const processedWorksheet: AdjustmentWorksheet = {
    id: adjustmentsWs.id,
    version: instanceVersion,
    items: items?.map((id, array_index) =>
      flattenWorksheetState(getWsById(worksheets, id), array_index),
    ),
  };

  setWorksheets(processedWorksheet);
};

/** Flattens an IWorksheetState for terser consumption
 * @param  {IWorksheetState} worksheet
 * @param  {number} array_index?
 */
const flattenWorksheetState = (worksheet: IWorksheetState, array_index?: number) => {
  const { instance } = worksheet;

  // The "id" property exists at both levels (IWorksheetState and IWorksheetState.instance), so rename the instance's id accordingly
  const instanceModified = { ...instance, instanceId: instance.id };

  // Clean up some keys
  delete instanceModified.id;

  return {
    ...worksheet,
    ...instanceModified,
    array_index,
  };
};
